import { EntityValueLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { Checkbox, Empty, Input, Modal } from "ant-design-vue";
import { fmtMsg } from "commonui";
import { debounce, difference, pull, union, uniq } from "lodash";
import { v4 } from "uuid";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { EntityLengths } from "../../conversation-builder-util";
import { ConversationNode, ConversationNodeType, Path } from "../../conversation-builder.model";
import { Entity, EntityValue } from "../../models/entity.model";

@Component({
	components: {
		Checkbox,
		CheckboxGroup: Checkbox.Group,
		Input,
		Empty
	}
})
export default class EntityValues extends Vue {
	@Prop() entityId!: string;
	@Prop() node!: ConversationNode<Path>;

	public EntityValueLocale = EntityValueLocale;

	public addInputValue: string | null = null;
	public addInputError: string | null = null;
	public entityValueError: string | null = null;
	public editDebounced = debounce(this.edit, 500);
	public checkAll: boolean = false;

	mounted() {
		const { isValid, message } = this.validateAll(this.entity.values, this.node.data.entityValueIds);
		this.entityValueError = message;
		this.checkAll = this.entity.values.every((value) => this.node.data.entityValueIds.includes(value.id));
	}

	private conversationStore = getModule(ConversationBuilderState);

	private get path() {
		return this.conversationStore.nodes[this.node.id] as ConversationNode<Path>;
	}

	public get entity(): Entity {
		return this.conversationStore.entities.directory[this.entityId];
	}

	public get isCommonEntity(): boolean {
		return !!this.entity.commonEntityId;
	}

	@Watch("entityId")
	@Watch("entity.values")
	@Watch("path.data.entityValueIds")
	onEntityIdChange() {
		const { isValid, message } = this.validateAll(this.entity.values, this.path.data.entityValueIds);
		this.entityValueError = message;
		this.checkAll = this.entity.values.every((value) => this.node.data.entityValueIds.includes(value.id));
	}

	public onValueCheck(ids: string[]) {
		const removedCurrentEntityValues = pull(
			this.node.data.entityValueIds,
			...this.entity.values.map((value) => value.id)
		);
		const entityValueIds = union(removedCurrentEntityValues, ids);
		this.conversationStore.updateNodeData<Path>({ id: this.node.id, data: { entityValueIds } });
		this.checkAll = ids.length === this.entity.values.length;
	}

	public addInputChange(event: InputEvent) {
		const target = event.target as HTMLInputElement;
		this.addInputValue = target.value;
		this.addInputError = null;
	}

	public add(): void {
		if (!this.addInputValue) {
			return;
		}

		const { isValid, message } = this.validate(this.addInputValue, this.entity.values);

		if (!isValid) {
			this.addInputError = message;
			return;
		} else {
			this.addInputError = message;
		}

		const entityValue: EntityValue = { id: v4(), key: 0, order: 0, value: this.addInputValue };
		this.conversationStore.updateEntity({
			id: this.entity.id,
			entity: { values: [...this.entity.values, entityValue] }
		});
		this.addInputValue = null;
	}

	public edit(event: InputEvent, entityValue: EntityValue): void {
		const target = event.target as HTMLInputElement;
		const text = target.value;
		const entityValues = this.entity.values.map((value) => {
			if (value.id === entityValue.id) {
				value.value = text;
			}
			return value;
		});
		this.conversationStore.updateEntity({
			id: this.entity.id,
			entity: { values: entityValues }
		});
	}

	private validate(inputValue: string, entityValues: EntityValue[]) {
		inputValue = inputValue.toLowerCase();
		const entityValuesString = entityValues.map((value) => value.value.toLowerCase());

		if (entityValuesString.includes(inputValue)) {
			return { isValid: false, message: fmtMsg(EntityValueLocale.EntityValueAlreadyExists) };
		}
		if (inputValue.length > EntityLengths.entity.values.maxLength || inputValue.length < EntityLengths.entity.values.minLength) {
			return { isValid: false, message: fmtMsg(EntityValueLocale.EntityValueLengthInvalid) };
		}

		return { isValid: true, message: "" };
	}

	validateAll(entityValues: EntityValue[], pathEntityValueIds: string[]) {
		let isValid = true;
		let message = "";
		const valueTexts = entityValues.map(v => v.value.trim().toLocaleLowerCase());
		const unTrimmedValueTexts = entityValues.map(v => v.value);
		const entityValueIds = entityValues.map(v => v.id);

		if (!entityValueIds || !entityValueIds.length) {
			isValid = false;
			message = fmtMsg(EntityValueLocale.EntityValueContainAtLeastOneValue);
		} else if (!pathEntityValueIds
			|| !pathEntityValueIds.length
			|| !entityValueIds.some(evId => pathEntityValueIds.includes(evId))) {
			isValid = false;
			message = fmtMsg(EntityValueLocale.EntityValueSelectAtLeastOneValue);
		} else if (!valueTexts.length) {
			isValid = false;
			message = fmtMsg(EntityValueLocale.EntityValueRequired);
		} else if (valueTexts.length !== uniq(valueTexts).length) {
			isValid = false;
			message = fmtMsg(EntityValueLocale.EntityValueUnique);
		} else if (unTrimmedValueTexts.some(v => v.length > EntityLengths.entity.values.maxLength || v.length < EntityLengths.entity.values.minLength)) {
			isValid = false;
			message = fmtMsg(EntityValueLocale.EntityValueLengthInvalid);
		}

		return { isValid, message };
	}

	public remove(entityValue: EntityValue) {
		const entityValues = this.entity.values.filter((value) => value.id !== entityValue.id);
		this.conversationStore.updateEntity({
			id: this.entity.id,
			entity: { values: entityValues }
		});
	}

	public removeValue(event: Event, entityValue: EntityValue) {
		event.preventDefault();

		const referencedNodeIds: string[] = [];
		for (const nodeId in this.conversationStore.nodes) {
			const node = this.conversationStore.nodes[nodeId];
			const isPath = node.type === ConversationNodeType.Path;
			if (isPath && (node.data as Path).entityValueIds.includes(entityValue.id)) {
				referencedNodeIds.push(node.id);
			}
		}

		if (referencedNodeIds.length) {
			this.removeEntityValueReferences(referencedNodeIds, entityValue.id);
		}
		this.remove(entityValue);
	}

	public removeEntityValueReferences(pathIds: string[], entityValueId: string): void {
		pathIds.forEach((pathId) => {
			const path = this.conversationStore.nodes[pathId] as ConversationNode<Path>;
			const entityValueIds = pull(path.data.entityValueIds, entityValueId);
			this.conversationStore.updateNodeData<Path>({ id: pathId, data: { entityValueIds } });
		});
	}

	get maxEntityValueLength() {
		return EntityLengths.entity.values.maxLength;
	}

	onCheckAllChange(e) {
		this.checkAll = e.target.checked;
		let entityValueIds: string[] = this.entity.values.map((value) => value.id);
		if (e.target.checked) {
			entityValueIds = uniq(entityValueIds.concat(this.node.data.entityValueIds));
		} else {
			entityValueIds = difference(this.node.data.entityValueIds, entityValueIds);
		}
		this.conversationStore.updateNodeData<Path>({ id: this.node.id, data: { entityValueIds } });
	}
}
